import { db, sitesCollectionRef, siteRef } from '../services/firebase'
import { getDocs, writeBatch, updateDoc, deleteDoc } from 'firebase/firestore'
import { EventViewModel, SiteViewModel } from '../pages/ViewModel'
import { getAuth } from 'firebase/auth'
import { getUserDetails } from './User'
import { isLatLngValid } from '../utils/isValid'
import { containsWhitespace } from '../utils'

let SITESDATA: SiteViewModel[] = []
let REGIONSDATA: string[] = []
let ZONESDATA: string[] = []
let CATCHMENTSDATA: string[] = []
let WATERWAYDATA: string[] = []
let BESTGAUGEOWNERDATA: string[] = []
let GAUGESTATIONDATA: string[] = []

export async function initiateSites() {
  if (SITESDATA.length !== 0) return

  await getAllSites()
}

export function getSite(siteCode: string): SiteViewModel | null {
  return SITESDATA.filter((data) => {
    return data.id === siteCode
  })[0]
}

export async function addSite(site: SiteViewModel) {
  validateSite(site)

  const isExisted = await isSiteCodeExisted(site.id)
  if (isExisted) throw new Error('Site code has already existed!')

  const batch = writeBatch(db)
  const {
    siteName,
    siteType,
    catchment,
    region,
    zone,
    waterway,
    latitude,
    longitude,
    bestGaugeOwner,
    gaugeStation,
    notes,
    publicAccess,
  } = site

  batch.set(siteRef(site.id.toUpperCase()), {
    siteName,
    siteType: siteType ?? 'surface water',
    catchment,
    region,
    zone,
    waterway,
    latitude,
    longitude,
    bestGaugeOwner,
    gaugeStation,
    notes,
    publicAccess: publicAccess ?? true,
  })

  await batch.commit()
}

export async function updateSite(site: SiteViewModel) {
  validateSite(site)

  const sendingSite = {
    ...site,
    siteType: site.siteType ?? 'surface water',
    publicAccess: site.publicAccess ?? true,
  }

  await updateDoc(siteRef(site.id.toUpperCase()), sendingSite)
}
/**
 * !!!!
 * Remove sites
 * Currently unavailable to delete 'events' subcollection
 */
export async function removeSite(id: string) {
  // delete all event's documents from site

  // delete site documents
  await deleteDoc(siteRef(id))
}

/**
 * @WARNING Make sure only admin have access to this function
 *
 */
export async function getAllSites(): Promise<SiteViewModel[]> {
  if (SITESDATA.length !== 0) return SITESDATA

  const sitesData: SiteViewModel[] = []
  const siteDocsSnap = await getDocs(sitesCollectionRef)

  const regionsData: string[] = []
  const zonesData: string[] = []
  const catchmentsData: string[] = []
  const waterwaysData: string[] = []
  const bestGaugeOwnersData: string[] = []
  const gaugeStationsData: string[] = []

  siteDocsSnap.forEach((siteDoc) => {
    const siteData = { ...siteDoc.data(), id: siteDoc.id } as SiteViewModel
    sitesData.push(siteData)

    // Filtering region
    if (siteData.region && regionsData.indexOf(siteData.region) === -1) {
      if (siteData.region.trim() !== '' || siteData.region.length !== 0)
        regionsData.push(siteData.region)
    }

    // Filtering catchment
    if (
      siteData.catchment &&
      catchmentsData.indexOf(siteData.catchment) === -1
    ) {
      if (siteData.catchment.trim() !== '' || siteData.catchment.length !== 0)
        catchmentsData.push(siteData.catchment)
    }

    // Filtering zone
    if (siteData.zone && zonesData.indexOf(siteData.zone) === -1) {
      if (siteData.zone.trim() !== '' || siteData.zone.length !== 0)
        zonesData.push(siteData.zone)
    }

    // Filtering water way
    if (siteData.waterway && waterwaysData.indexOf(siteData.waterway) === -1) {
      if (siteData.waterway.trim() !== '' || siteData.waterway.length !== 0)
        waterwaysData.push(siteData.waterway)
    }

    // Filtering best gauge owner
    if (
      siteData.bestGaugeOwner &&
      bestGaugeOwnersData.indexOf(siteData.bestGaugeOwner) === -1
    ) {
      if (
        siteData.bestGaugeOwner.trim() !== '' ||
        siteData.bestGaugeOwner.length !== 0
      )
        bestGaugeOwnersData.push(siteData.bestGaugeOwner)
    }

    // Filtering gauge station
    if (
      siteData.gaugeStation &&
      gaugeStationsData.indexOf(siteData.gaugeStation) === -1
    ) {
      if (
        siteData.gaugeStation.trim() !== '' ||
        siteData.gaugeStation.length !== 0
      )
        gaugeStationsData.push(siteData.gaugeStation)
    }
  })

  SITESDATA = sitesData
  REGIONSDATA = regionsData
  CATCHMENTSDATA = catchmentsData
  ZONESDATA = zonesData
  WATERWAYDATA = waterwaysData
  BESTGAUGEOWNERDATA = bestGaugeOwnersData
  GAUGESTATIONDATA = gaugeStationsData

  return sitesData
}

// This function is not in used, it's getting developed to remove 'sites' in AuthContext
export async function getSitesForUser() {
  const auth = getAuth()
  const user = auth.currentUser
  const userDetails = await getUserDetails(user.uid)
  if (user) {
    const regions_accessed = userDetails.role.regions_accessed ?? []
    // Filtering out specified regions for a user
    if (regions_accessed.length === 0) {
      return SITESDATA
    } else {
      const filteredSitesData = SITESDATA.filter((site) => {
        return regions_accessed.includes(site.region)
      })
      return filteredSitesData
    }
  } else {
    return []
  }
}

export function searchSite(
  sites: SiteViewModel[],
  query: string
): SiteViewModel[] {
  const lowerQuery = query.toLowerCase()

  if (query.length === 1) {
    return sites.filter((data) => {
      const { siteName } = data
      return siteName.toLowerCase().substring(0, 1) === lowerQuery
    })
  }

  return sites
    .filter((data) => {
      const {
        id,
        siteName,
        region,
        catchment,
        waterway,
        zone,
        bestGaugeOwner,
        gaugeStation,
      } = data
      return (
        id.toLowerCase().match(lowerQuery) ||
        siteName.toLowerCase().match(lowerQuery) ||
        region.toLowerCase().match(lowerQuery) ||
        catchment?.toLowerCase().match(lowerQuery) ||
        waterway?.toLowerCase().match(lowerQuery) ||
        zone?.toLowerCase().match(lowerQuery) ||
        bestGaugeOwner?.toLowerCase().match(lowerQuery) ||
        gaugeStation?.match(lowerQuery)
      )
    })
    .slice(0, 30)
}

/**
 * All different regions
 *
 * Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllRegions(): string[] {
  return REGIONSDATA
}

/**
 * All different zones
 *
 * !!Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllZones(): string[] {
  return ZONESDATA
}

/**
 * All different catchments
 *
 * !!Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllCatchments(): string[] {
  return CATCHMENTSDATA
}

/**
 * All different waterways
 *
 * !!Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllWaterways(): string[] {
  return WATERWAYDATA
}

/**
 * All different gauge owners
 *
 * !!Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllBestGaugeOnwers(): string[] {
  return BESTGAUGEOWNERDATA
}

/**
 * All different gauge station
 *
 * !!Able to call this function after sites is initiated and stored in session
 *
 */
export function getAllGaugeStations(): string[] {
  return GAUGESTATIONDATA
}

function validateSite(site: SiteViewModel) {
  if (!site.id && site.id.trim().length === 0)
    throw new Error('Empty site code')
  if (containsWhitespace(site.id))
    throw new Error('No whitespace in site code!')
  if (!site.siteName && site.siteName.trim().length === 0)
    throw new Error('Empty site name')
  if (!site.siteType && site.siteType.trim().length === 0)
    throw new Error('Empty site type')
  if (!site.region && site.region.trim().length === 0)
    throw new Error('Empty region')
  if (!site.catchment && site.catchment.trim().length === 0)
    throw new Error('Empty catchment')
  if (!site.zone && site.zone.trim().length === 0) throw new Error('Empty zone')
  if (!site.bestGaugeOwner && site.bestGaugeOwner.trim().length === 0)
    throw new Error('Empty Best Gauge Owner')
  if (!site.gaugeStation && site.gaugeStation.trim().length === 0)
    throw new Error('Empty gauge station')
  if (!isLatLngValid(site.latitude, site.longitude))
    throw new Error('Invalid latitude, longitude')
}

async function isSiteCodeExisted(siteCode: string) {
  const sites = await getAllSites()
  const siteCodes = sites.map((s) => s.id)

  return siteCodes.includes(siteCode)
}
